import React from 'react';
import { CTASection } from '../../components/CTASection';
import { FAQSection } from '../../components/FAQSection';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { AlertCircle, Activity, Stethoscope, TrendingUp, BookOpen, FileText } from 'lucide-react';
import { useSite } from '../../contexts/SiteContext';

export const ClavicleFracture = () => {
  const { theme } = useSite();
  
  const faqs = [
    { question: 'How long does a clavicle fracture take to heal?', answer: 'Most clavicle fractures heal in 6-12 weeks. Midshaft fractures (most common) typically heal in 8-12 weeks. Distal clavicle fractures may take longer (10-16 weeks) due to less blood supply. Children heal faster (4-6 weeks). Healing is monitored with X-rays at regular intervals.' },
    { question: 'Do all clavicle fractures need surgery?', answer: 'No. 85-90% of clavicle fractures heal well without surgery. Surgery is recommended for: significantly displaced fractures (>2cm shortening), open fractures, fractures with skin tenting, associated neurovascular injury, or distal clavicle fractures with ligament injury. Non-operative treatment with a sling achieves excellent results in most cases.' },
    { question: 'What are the risks of not having surgery?', answer: 'For appropriately selected non-operative cases, outcomes are excellent. However, displaced midshaft fractures treated non-operatively have 15-20% nonunion rate vs 2-5% with surgery. Some patients may have residual bump at fracture site (cosmetic) or mild shortening. Your surgeon will discuss individual risks based on your fracture pattern.' },
    { question: 'When can I return to sports after a clavicle fracture?', answer: 'Non-contact activities: 6-8 weeks when pain-free. Contact sports: 12-16 weeks minimum (bone needs full strength). Post-surgery timelines are similar but may allow earlier return with protected healing. Return is guided by X-ray healing, pain-free range of motion, and strength recovery.' },
    { question: 'Will I have a bump after healing?', answer: 'A visible bump is common after clavicle fracture healing, especially with non-operative treatment. This represents bone callus formation and typically remodels (becomes smaller) over 6-12 months. It does not affect function. Surgical fixation generally results in less visible bump but leaves a scar.' },
    { question: 'What is the best sleeping position?', answer: 'Sleep semi-reclined (in a recliner or propped with pillows) for the first 1-2 weeks. This reduces pain from lying flat. Sleep on your back or uninjured side. Avoid sleeping on the injured side. Use a sling at night initially for comfort and protection.' }
  ];

  return (
    <div className="min-h-screen" data-testid="clavicle-fracture-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: `${theme?.primaryHex}20`, color: theme?.primaryHex }}>
              Trauma & Fractures
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Clavicle Fracture Treatment in Hyderabad
            </h1>
            <p className="text-xl text-gray-600">
              Surgery & recovery by Dr. Deepthi Nandan Reddy at Apollo Hospitals
            </p>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding Clavicle Fractures</h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                The clavicle (collarbone) is the most commonly fractured bone in the body, accounting for 2.5-5% of all fractures. It connects the arm to the body and protects vital structures underneath.
              </p>
              <p>
                <strong>Epidemiology:</strong> Peak incidence in young males (sports/accidents) and elderly females (falls). 80% occur in the middle third (midshaft), 15% in the lateral (distal) third, and 5% in the medial third.
              </p>
              <p>
                <strong>Mechanism:</strong> Most commonly from a fall onto the shoulder (87%) or direct blow. The classic presentation is a painful, swollen, drooping shoulder after trauma.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Classification */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Classification (Allman/Neer)</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-t-4" style={{ borderTopColor: theme?.primaryHex }}>
                <CardHeader>
                  <CardTitle>Group I: Midshaft (80%)</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-600 space-y-2">
                  <p>• Most common location</p>
                  <p>• Good healing potential</p>
                  <p>• Usually non-operative</p>
                  <p>• Surgery if displaced &gt;2cm</p>
                </CardContent>
              </Card>
              <Card className="border-t-4" style={{ borderTopColor: theme?.primaryHex }}>
                <CardHeader>
                  <CardTitle>Group II: Distal (15%)</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-600 space-y-2">
                  <p>• Higher nonunion rate</p>
                  <p>• May involve AC joint</p>
                  <p>• Type II most unstable</p>
                  <p>• Often needs surgery</p>
                </CardContent>
              </Card>
              <Card className="border-t-4" style={{ borderTopColor: theme?.primaryHex }}>
                <CardHeader>
                  <CardTitle>Group III: Medial (5%)</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-600 space-y-2">
                  <p>• Least common</p>
                  <p>• Usually stable</p>
                  <p>• Near vital structures</p>
                  <p>• Mostly non-operative</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Treatment Options</h2>
            </div>
            
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r">
              <div className="flex items-start gap-2">
                <BookOpen className="w-5 h-5 text-blue-600 mt-0.5" />
                <p className="text-sm text-blue-800">
                  Treatment based on Canadian Orthopaedic Trauma Society (COTS) trial, Edinburgh studies, and AAOS guidelines.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <Card className="border-l-4 border-l-green-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">FIRST LINE</span>
                    Non-Operative Treatment
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-3">
                  <p><strong>Indications:</strong> Non-displaced or minimally displaced fractures, medial third fractures, patients with low functional demands, or high surgical risk.</p>
                  <p><strong>Protocol:</strong></p>
                  <p>• Sling for comfort (2-4 weeks)</p>
                  <p>• Early gentle range of motion as pain allows</p>
                  <p>• Progressive strengthening from 6 weeks</p>
                  <p>• Full activity by 12 weeks if healed</p>
                  <p className="bg-green-50 p-3 rounded mt-3 text-sm">
                    <strong>Success Rate:</strong> 85-95% union rate for appropriately selected cases. Most patients return to full function.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="border-l-4 border-l-orange-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">SURGICAL</span>
                    Operative Treatment
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-3">
                  <p><strong>Indications:</strong></p>
                  <p>• Displaced midshaft fractures (&gt;2cm shortening)</p>
                  <p>• Open fractures or skin tenting</p>
                  <p>• Neurovascular compromise</p>
                  <p>• Floating shoulder (ipsilateral scapula neck fracture)</p>
                  <p>• Distal clavicle fractures with CC ligament disruption</p>
                  
                  <p className="mt-4"><strong>Surgical Options:</strong></p>
                  <p>• <strong>Plate Fixation:</strong> Superior or anteroinferior plating. Gold standard for displaced midshaft fractures. 95-98% union rate.</p>
                  <p>• <strong>Intramedullary Fixation:</strong> Titanium elastic nail or rigid nail. Less soft tissue stripping. Good for simple patterns.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Complications */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Potential Complications</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle>Non-Operative Risks</CardTitle></CardHeader>
                <CardContent className="text-gray-600 space-y-2">
                  <p>• Nonunion: 15-20% (displaced fractures)</p>
                  <p>• Malunion with shortening</p>
                  <p>• Cosmetic bump</p>
                  <p>• Persistent pain (rare)</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Surgical Risks</CardTitle></CardHeader>
                <CardContent className="text-gray-600 space-y-2">
                  <p>• Nonunion: 2-5%</p>
                  <p>• Infection: 1-2%</p>
                  <p>• Hardware irritation: 10-30%</p>
                  <p>• Scar (visible location)</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* References */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-gray-500" />
              <h3 className="text-xl font-bold text-gray-700">Scientific References</h3>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg text-sm text-gray-600 space-y-2">
              <p>1. Canadian Orthopaedic Trauma Society (COTS). Nonoperative vs operative treatment of displaced midshaft clavicle fractures. JBJS 2007</p>
              <p>2. Robinson CM. Fractures of the clavicle in the adult. JBJS Br 1998</p>
              <p>3. McKee MD, et al. Operative versus nonoperative care of displaced midshaft clavicular fractures. JBJS 2012</p>
              <p className="text-xs text-gray-400 mt-4 pt-4 border-t">
                Last Updated: January 2025 | Content reviewed by Dr. Deepthi Nandan Reddy, FRCS (Ortho)
              </p>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <CTASection />
    </div>
  );
};

export default ClavicleFracture;
